/**
 * ADServiceOuSection — Árbol de unidades organizativas (OU) del dominio.
 *
 * Las GPO se vinculan a un contenedor; hasta ahora ese destino era texto libre,
 * así que no había forma de documentar la estructura real del dominio ni de
 * saber a qué OU apunta cada directiva. Aquí se define el árbol de OUs y cada
 * GPO se vincula a una de ellas (`target_ou_id`), de modo que renombrar o mover
 * una OU actualiza el DN que tienen como destino sus GPO.
 *
 * El DN se calcula a partir del nombre del dominio del servicio
 * (`corp.local` → `DC=corp,DC=local`), igual que hace el backend.
 */
import React, { useMemo } from 'react';
import {
  PlusIcon, TrashIcon, FolderIcon, FolderOpenIcon, DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';

export function domainDn(domainName) {
  return String(domainName || '')
    .split('.')
    .map(p => p.trim())
    .filter(Boolean)
    .map(p => `DC=${p}`)
    .join(',');
}

/** {ou_id: DN} replicando el cálculo del backend. */
export function buildOuDns(ous, domainName) {
  const suffix = domainDn(domainName);
  const byId = {};
  (ous || []).forEach(o => { byId[o.id] = o; });

  const out = {};
  (ous || []).forEach(ou => {
    const parts = [];
    const seen = new Set();
    let cur = ou;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      parts.push(`OU=${cur.name}`);
      cur = byId[cur.parent_id];
    }
    let dn = parts.join(',');
    if (suffix) dn = dn ? `${dn},${suffix}` : suffix;
    out[ou.id] = dn;
  });
  return out;
}

function depthOfOu(ou, byId) {
  let depth = 0;
  const seen = new Set([ou.id]);
  let cur = byId[ou.parent_id];
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    depth += 1;
    cur = byId[cur.parent_id];
  }
  return depth;
}

function newOuId() {
  return `ou-${Math.random().toString(36).slice(2, 9)}`;
}

export default function ADServiceOuSection({
  ous, onChange, gpos = [], sites = [], domainName = '', presets = [], editMode,
  domainIsFallback = false,
}) {
  const rows = ous || [];

  const byId = useMemo(() => {
    const map = {};
    rows.forEach(o => { map[o.id] = o; });
    return map;
  }, [rows]);

  const dns = useMemo(() => buildOuDns(rows, domainName), [rows, domainName]);

  const gpoCount = useMemo(() => {
    const map = {};
    (gpos || []).forEach(g => {
      if (g.target_ou_id) map[g.target_ou_id] = (map[g.target_ou_id] || 0) + 1;
    });
    return map;
  }, [gpos]);

  const childrenOf = (id) => rows.filter(o => (o.parent_id || null) === id);

  const addOu = (parentId = null, name = '') => onChange([
    ...rows,
    {
      id: newOuId(), name, parent_id: parentId,
      description: '', site: '',
    },
  ]);

  const addPresets = () => {
    const existing = new Set(rows.map(o => (o.name || '').toLowerCase()).filter(Boolean));
    const missing = (presets || []).filter(p => !existing.has(p.toLowerCase()));
    if (missing.length === 0) return;
    onChange([
      ...rows,
      ...missing.map(name => ({
        id: newOuId(), name, parent_id: null, description: '', site: '',
      })),
    ]);
  };

  const update = (idx, patch) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const remove = (idx) => {
    const target = rows[idx];
    const parentId = target.parent_id || null;
    // Los hijos suben al padre de la OU borrada (no se pierde el subárbol)
    const next = rows
      .filter((_, i) => i !== idx)
      .map(o => (o.parent_id === target.id ? { ...o, parent_id: parentId } : o));
    onChange(next);
  };

  const shell = {
    background: 'rgba(250,204,21,.07)', border: '1px solid rgba(250,204,21,.35)',
    borderRadius: 8, padding: 12,
  };
  const labelStyle = { fontSize: '0.7rem' };
  const dnStyle = {
    fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)',
    wordBreak: 'break-all',
  };

  const totalChildren = rows.length;

  return (
    <div style={shell}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Unidades organizativas (OUs)
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {totalChildren}
          </span>
        </div>
        {editMode && (
          <>
            {presets && presets.length > 0 && (
              <button className="dv-btn" type="button" onClick={addPresets}
                      title={`Añade las que falten: ${(presets || []).join(', ')}`}>
                <DocumentDuplicateIcon style={{ width: 14, height: 14 }} /> Estructura típica
              </button>
            )}
            <button className="dv-btn" type="button" onClick={() => addOu(null)}>
              <PlusIcon style={{ width: 14, height: 14 }} /> Añadir OU
            </button>
          </>
        )}
      </div>

      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: 10 }}>
        Raíz del dominio: <code style={dnStyle}>{domainDn(domainName) || '(define el dominio raíz del servicio)'}</code>
        {' '}· las GPO se vinculan a una OU del catálogo desde su sección.
        {domainIsFallback && (
          <div style={{ color: '#fbbf24', marginTop: 4 }}>
            ⚠ El dominio raíz del servicio está vacío: se está usando el nombre del servicio
            como dominio, así que estos DN no son los reales.
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin OUs documentadas. Añádelas para poder vincular las GPO a un contenedor real.
        </div>
      ) : editMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((ou, idx) => {
            const depth = depthOfOu(ou, byId);
            const kids = childrenOf(ou.id).length;
            const linked = gpoCount[ou.id] || 0;
            return (
              <div
                key={ou.id}
                className="row g-2"
                style={{
                  alignItems: 'flex-end',
                  paddingLeft: depth * 22,
                  borderLeft: depth > 0 ? '2px solid rgba(250,204,21,.35)' : 'none',
                }}
              >
                <div className="col-md-3">
                  <label className="form-label" style={labelStyle}>
                    {depth === 0 ? 'OU' : 'Sub-OU'}
                  </label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ej. Servidores"
                    value={ou.name || ''}
                    onChange={e => update(idx, { name: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={labelStyle}>Sede (opcional)</label>
                  <select
                    className="form-select form-select-sm"
                    value={ou.site || ''}
                    onChange={e => update(idx, { site: e.target.value })}
                  >
                    <option value="">— Todas —</option>
                    {sites.map(s => (
                      <option key={s.key || s.name} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label" style={labelStyle}>Descripción</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ej. Servidores miembro"
                    value={ou.description || ''}
                    onChange={e => update(idx, { description: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label" style={labelStyle}>Distinguished Name</label>
                  <div style={{
                    ...dnStyle, padding: '3px 6px', background: 'var(--bg-input)',
                    borderRadius: 4, minHeight: 26,
                  }}>
                    {dns[ou.id] || '—'}
                  </div>
                </div>
                <div className="col-auto d-flex align-items-center gap-1">
                  <button className="dv-btn" type="button" title="Añadir sub-OU dentro de esta"
                          onClick={() => addOu(ou.id)}>
                    <FolderOpenIcon style={{ width: 14, height: 14 }} />
                  </button>
                  <button
                    className="dv-btn"
                    type="button"
                    title={linked > 0
                      ? `Eliminar (${linked} GPO se quedan sin vínculo)`
                      : (kids > 0 ? 'Eliminar (las sub-OUs suben al padre)' : 'Eliminar')}
                    onClick={() => remove(idx)}
                  >
                    <TrashIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
                {(linked > 0 || kids > 0) && (
                  <div className="col-12" style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: -2 }}>
                    {kids > 0 && <>{kids} sub-OU{kids === 1 ? '' : 's'} · </>}
                    {linked > 0 && <>{linked} GPO vinculada{linked === 1 ? '' : 's'}</>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rows.map(ou => {
            const depth = depthOfOu(ou, byId);
            const linked = gpoCount[ou.id] || 0;
            return (
              <div key={ou.id} style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                paddingLeft: depth * 22 + 6,
              }}>
                {depth === 0
                  ? <FolderIcon style={{ width: 15, height: 15, color: '#fbbf24' }} />
                  : <FolderOpenIcon style={{ width: 14, height: 14, color: 'var(--text-muted)' }} />}
                <strong style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>{ou.name}</strong>
                {ou.site && (
                  <span style={{
                    fontSize: '0.68rem', background: 'rgba(59,130,246,.15)', color: '#93c5fd',
                    borderRadius: 4, padding: '0 6px',
                  }}>
                    📍 {ou.site}
                  </span>
                )}
                {linked > 0 && (
                  <span style={{
                    fontSize: '0.68rem', background: 'rgba(139,92,246,.15)', color: '#c4b5fd',
                    borderRadius: 4, padding: '0 6px',
                  }}>
                    {linked} GPO
                  </span>
                )}
                {ou.description && (
                  <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{ou.description}</span>
                )}
                <span style={dnStyle}>{ou.dn || dns[ou.id]}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
